/**
 * Premium "Glitch Intro" — RGB split + datamosh blocks.
 *
 * Visual design:
 *  - Headline rendered three times in cyan / magenta / yellow (CMY/RGB-style
 *    chromatic aberration). The three layers jitter on different frequencies
 *    and converge to a clean white headline.
 *  - Three horizontal "scan slice" bars animate across, breaking the title
 *    into shifted strips for a few frames.
 *  - Static-noise overlay flickers during glitch then settles.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, svgDataUrl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const glitchIntro: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "SYSTEM ONLINE";
  const sub = inputs.subtext ?? "";
  // Optional status-pill text above the headline. Empty string => no pill.
  // Previously this was hardcoded to "// signal locked" which made every
  // glitchIntro show an unwanted brand-color eyebrow at the top.
  const status = (inputs.number ?? "").trim();
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.62;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const eyeSize = typeSize("sm", ctx.format) * 0.7;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const titleStackId = `${clipPrefix}-stack`;
  const cyanId = `${clipPrefix}-cyan`;
  const magId = `${clipPrefix}-mag`;
  const whiteId = `${clipPrefix}-white`;
  const slice1Id = `${clipPrefix}-slc1`;
  const slice2Id = `${clipPrefix}-slc2`;
  const slice3Id = `${clipPrefix}-slc3`;
  const noiseId = `${clipPrefix}-noise`;
  const headBoxId = `${clipPrefix}-headbox`;
  const subId = `${clipPrefix}-sub`;
  const eyeId = `${clipPrefix}-eye`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  const baseStyle = `position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.9;letter-spacing:-3px;text-align:center;text-wrap:balance;`;

  const statusBlock = status
    ? `<div id="${eyeId}" style="display:inline-flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:8px;text-transform:uppercase;color:${accent};opacity:0;padding:8px 18px;border-radius:9999px;background:${withAlpha(accent, 0.12)};border:1px solid ${withAlpha(accent, 0.35)};">
        <span style="width:10px;height:10px;border-radius:9999px;background:${accent};box-shadow:0 0 12px ${accent};"></span>
        <span>${escapeHtml(status)}</span>
      </div>`
    : "";

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div id="${noiseId}" style="position:absolute;inset:0;background-image:${svgDataUrl(`<svg xmlns="http://www.w3.org/2000/svg" width="320" height="320"><filter id="n"><feTurbulence baseFrequency="2.6" numOctaves="2" stitchTiles="stitch"/></filter><rect width="100%" height="100%" filter="url(%23n)" opacity="0.95"/></svg>`)};opacity:0.12;mix-blend-mode:overlay;pointer-events:none;z-index:7;"></div>

  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 36 : 28}px;padding:96px;">
    ${statusBlock}

    <div id="${headBoxId}" style="position:relative;width:${isVertical ? 920 : 1500}px;max-width:90vw;height:${headSize * 1.4}px;">
      <div id="${titleStackId}" style="position:absolute;inset:0;mix-blend-mode:screen;">
        <div id="${cyanId}" style="${baseStyle}color:#22d3c5;text-shadow:0 0 16px #22d3c5;">${escapeHtml(headline)}</div>
        <div id="${magId}" style="${baseStyle}color:#a855f7;text-shadow:0 0 16px #a855f7;">${escapeHtml(headline)}</div>
        <div id="${whiteId}" style="${baseStyle}color:${themeColors.fg};text-shadow:0 0 24px ${withAlpha(themeColors.fg, 0.55)};">${escapeHtml(headline)}</div>
      </div>

      <div id="${slice1Id}" style="position:absolute;left:0;right:0;top:18%;height:14%;overflow:hidden;color:${themeColors.fg};${baseStyle}font-size:${headSize}px;">${escapeHtml(headline)}</div>
      <div id="${slice2Id}" style="position:absolute;left:0;right:0;top:46%;height:12%;overflow:hidden;color:${themeColors.fg};${baseStyle}font-size:${headSize}px;">${escapeHtml(headline)}</div>
      <div id="${slice3Id}" style="position:absolute;left:0;right:0;top:72%;height:14%;overflow:hidden;color:${themeColors.fg};${baseStyle}font-size:${headSize}px;">${escapeHtml(headline)}</div>
    </div>

    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.35;opacity:0;">${escapeHtml(sub)}</div>` : ""}
  </div>
</div>`;

  const init = [
    `gsap.set("#${cyanId}", { x: -22, opacity: 0 });`,
    `gsap.set("#${magId}", { x: 22, opacity: 0 });`,
    `gsap.set("#${whiteId}", { opacity: 0 });`,
    `gsap.set("#${slice1Id}", { x: 0, opacity: 0 });`,
    `gsap.set("#${slice2Id}", { x: 0, opacity: 0 });`,
    `gsap.set("#${slice3Id}", { x: 0, opacity: 0 });`,
    status ? `gsap.set("#${eyeId}", { opacity: 0, y: -12 });` : "",
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    // Glitch-in burst.
    t.to(`#${cyanId}`, { opacity: 1, duration: 0.05, ease: "raw:\"none\"" }, 0.05),
    t.to(`#${magId}`, { opacity: 1, duration: 0.05, ease: "raw:\"none\"" }, 0.05),
    t.to(`#${whiteId}`, { opacity: 1, duration: 0.05, ease: "raw:\"none\"" }, 0.05),
    // Slice strips jitter horizontally.
    t.to(`#${slice1Id}`, { opacity: 1, duration: 0.05 }, 0.1),
    t.to(`#${slice2Id}`, { opacity: 1, duration: 0.05 }, 0.1),
    t.to(`#${slice3Id}`, { opacity: 1, duration: 0.05 }, 0.1),
    t.to(`#${slice1Id}`, { x: -32, duration: 0.07, ease: "raw:\"none\"", repeat: 5, yoyo: true }, 0.1),
    t.to(`#${slice2Id}`, { x: 28, duration: 0.06, ease: "raw:\"none\"", repeat: 6, yoyo: true }, 0.1),
    t.to(`#${slice3Id}`, { x: -18, duration: 0.05, ease: "raw:\"none\"", repeat: 7, yoyo: true }, 0.1),
    // RGB layers jitter then converge.
    t.to(`#${cyanId}`, { x: -34, duration: 0.06, repeat: 5, yoyo: true, ease: "raw:\"none\"" }, 0.1),
    t.to(`#${magId}`, { x: 34, duration: 0.06, repeat: 5, yoyo: true, ease: "raw:\"none\"" }, 0.1),
    // Noise flicker.
    t.fromTo(`#${noiseId}`, { opacity: 0.45 }, { opacity: 0.18, duration: 0.05, repeat: 8, yoyo: true, ease: "raw:\"none\"" }, 0.05),
    // Slices fade out as glitch resolves.
    t.to(`#${slice1Id}`, { opacity: 0, duration: 0.3, ease: "raw:\"power2.out\"" }, 0.7),
    t.to(`#${slice2Id}`, { opacity: 0, duration: 0.3, ease: "raw:\"power2.out\"" }, 0.7),
    t.to(`#${slice3Id}`, { opacity: 0, duration: 0.3, ease: "raw:\"power2.out\"" }, 0.7),
    // RGB converge.
    t.to(`#${cyanId}`, { x: 0, opacity: 0.45, duration: 0.4, ease: "raw:\"power3.out\"" }, 0.7),
    t.to(`#${magId}`, { x: 0, opacity: 0.45, duration: 0.4, ease: "raw:\"power3.out\"" }, 0.7),
    t.to(`#${noiseId}`, { opacity: 0.08, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.85),
  ];
  if (status) tweens.push(t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.4, ease: "raw:\"power2.out\"" }, 0.95));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.15));

  // Random subtle glitch flicker during dwell.
  const dwell = Math.max(0.6, clipDurationSec - 1.5);
  tweens.push(t.to(`#${cyanId}`, { x: -4, duration: 0.12, repeat: Math.floor(dwell / 0.6), yoyo: true, ease: "raw:\"none\"" }, 1.5));
  tweens.push(t.to(`#${magId}`, { x: 4, duration: 0.12, repeat: Math.floor(dwell / 0.6), yoyo: true, ease: "raw:\"none\"" }, 1.5));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
